import { useSyncExternalStore } from 'react'
import { create } from 'zustand'
import type { SensorPlacement } from '@/features/ble/types'

export type Theme = 'dark' | 'light' | 'system'
export type ResolvedTheme = 'dark' | 'light'
export type LabScene = 'default' | 'health' | 'robotics'

const THEME_STORAGE_KEY = 'neeno-lab-theme'
const SCENE_STORAGE_KEY = 'neeno-lab-scene'
const SENSOR_PLACEMENT_STORAGE_KEY = 'neeno-lab-sensor-placement'

function getInitialTheme(): Theme {
  if (typeof window === 'undefined') {
    return 'light'
  }

  const storedTheme = window.localStorage.getItem(THEME_STORAGE_KEY)
  return storedTheme === 'dark' || storedTheme === 'light' || storedTheme === 'system'
    ? storedTheme
    : 'system'
}

function getInitialScene(): LabScene {
  if (typeof window === 'undefined') {
    return 'default'
  }

  const storedScene = window.localStorage.getItem(SCENE_STORAGE_KEY)
  return storedScene === 'health' || storedScene === 'robotics' ? storedScene : 'default'
}

function getInitialSensorPlacement(): SensorPlacement {
  if (typeof window === 'undefined') {
    return 'leftArm'
  }

  const storedPlacement = window.localStorage.getItem(SENSOR_PLACEMENT_STORAGE_KEY)
  if (storedPlacement === 'rightHand') {
    return 'rightHand'
  }

  if (storedPlacement === 'leftHand') {
    return 'leftHand'
  }

  if (storedPlacement === 'rightArm' || storedPlacement === 'rightForearm') {
    return 'rightArm'
  }

  return 'leftArm'
}

function persistTheme(theme: Theme) {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme)
  }
}

function persistScene(scene: LabScene) {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(SCENE_STORAGE_KEY, scene)
  }
}

function persistSensorPlacement(placement: SensorPlacement) {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(SENSOR_PLACEMENT_STORAGE_KEY, placement)
  }
}

interface UiStoreState {
  sidebarOpen: boolean
  debugConsoleOpen: boolean
  scene: LabScene
  theme: Theme
  sensorPlacement: SensorPlacement
  selectedSensor: SensorPlacement | null
  frameCorrection: boolean
  setSidebarOpen: (open: boolean) => void
  setDebugConsoleOpen: (open: boolean) => void
  toggleSidebar: () => void
  toggleDebugConsole: () => void
  setScene: (scene: LabScene) => void
  setTheme: (theme: Theme) => void
  setSensorPlacement: (placement: SensorPlacement) => void
  setSelectedSensor: (placement: SensorPlacement | null) => void
  toggleFrameCorrection: () => void
}

export const useUiStore = create<UiStoreState>((set) => ({
  sidebarOpen: true,
  debugConsoleOpen: false,
  scene: getInitialScene(),
  theme: getInitialTheme(),
  sensorPlacement: getInitialSensorPlacement(),
  selectedSensor: null,
  frameCorrection: true,
  setSidebarOpen: (open) => set({ sidebarOpen: open }),
  setDebugConsoleOpen: (open) => set({ debugConsoleOpen: open }),
  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
  toggleDebugConsole: () => set((s) => ({ debugConsoleOpen: !s.debugConsoleOpen })),
  setScene: (scene) => {
    persistScene(scene)
    set({ scene, selectedSensor: null })
  },
  setTheme: (theme) => {
    persistTheme(theme)
    set({ theme })
  },
  setSensorPlacement: (placement) => {
    persistSensorPlacement(placement)
    set({ sensorPlacement: placement })
  },
  setSelectedSensor: (selectedSensor) => set({ selectedSensor }),
  toggleFrameCorrection: () => set((s) => ({ frameCorrection: !s.frameCorrection })),
}))

const SYSTEM_DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)'

function subscribeSystemTheme(callback: () => void): () => void {
  const media = window.matchMedia(SYSTEM_DARK_MEDIA_QUERY)
  media.addEventListener('change', callback)
  return () => media.removeEventListener('change', callback)
}

function getSystemThemeSnapshot(): boolean {
  return window.matchMedia(SYSTEM_DARK_MEDIA_QUERY).matches
}

function getSystemThemeServerSnapshot(): boolean {
  return false
}

/**
 * Resolve the user's theme preference ('dark' | 'light' | 'system') into a
 * concrete 'dark' or 'light' value. In 'system' mode it follows the OS colour
 * scheme and stays reactive to OS-level changes.
 */
export function useResolvedTheme(): ResolvedTheme {
  const theme = useUiStore((s) => s.theme)
  const systemPrefersDark = useSyncExternalStore(
    subscribeSystemTheme,
    getSystemThemeSnapshot,
    getSystemThemeServerSnapshot,
  )

  if (theme === 'system') {
    return systemPrefersDark ? 'dark' : 'light'
  }

  return theme
}
